import './styles/widget.css';
import { WidgetApi, ApiError } from './api.js';
import { createStore } from './state.js';
import { handleKey } from './components/keyboard.js';
import { SearchBox } from './components/search-box.js';
import { Dropdown } from './components/dropdown.js';
import { InlinePanel } from './components/inline-panel.js';

const SESSION_KEY = 'doka-session-id';
const VISITOR_KEY = 'dokasearch_vid';

/**
 * @returns {{publicKey: string, baseUrl: string, selector: string,
 *            debounceMs: number, minLength: number, perPage: number,
 *            theme: 'light'|'dark'}}
 */
function readConfig() {
    const script = document.currentScript ?? lastWidgetScript();
    if (!script) {
        return { publicKey: '', baseUrl: '', selector: '', debounceMs: 250, minLength: 2, perPage: 12, theme: 'light', layout: 'overlay' };
    }
    const src = script.getAttribute('src') || '';
    let baseUrl = '';
    try {
        const u = new URL(src, window.location.href);
        baseUrl = `${u.protocol}//${u.host}`;
    } catch {
        baseUrl = '';
    }
    return {
        publicKey: script.dataset.publicKey || '',
        baseUrl: script.dataset.baseUrl || baseUrl,
        selector: script.dataset.input || '',
        debounceMs: parseInt(script.dataset.debounce || '250', 10),
        minLength: parseInt(script.dataset.minLength || '2', 10),
        perPage: Math.min(parseInt(script.dataset.perPage || '12', 10), 24),
        theme: script.dataset.theme === 'dark' ? 'dark' : 'light',
        layout: script.dataset.layout === 'inline' ? 'inline' : 'overlay',
    };
}

function lastWidgetScript() {
    const all = document.querySelectorAll('script[src*="widget.js"]');
    return all[all.length - 1] || null;
}

function getSessionId() {
    try {
        let id = localStorage.getItem(SESSION_KEY);
        if (!id) {
            id = (crypto.randomUUID && crypto.randomUUID())
                || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        return `s-${Date.now()}`;
    }
}

/**
 * BL-D1 — долгоживущий (365 дней) browser-стабильный идентификатор посетителя.
 * UUID v4 из `crypto.randomUUID()`, если доступен; иначе откатывается на форму
 * timestamp+random, которая всё ещё достаточно глобально уникальна для
 * HLL (~1% погрешности терпит гораздо более слабую уникальность). Возвращается как
 * строка, чтобы SearchController трактовал его как один Redis HLL term.
 *
 * Хранилище: `localStorage` по ключу `dokasearch_vid`. Переживает навигацию
 * + закрытие вкладки; сбрасывается при privacy-действиях пользователя (очистка кэша / расширение).
 * Без cookie — разрыв origin между доменом магазина ↔ origin виджета потребовал бы
 * разрешения на third-party-cookie, которое большинство браузеров сейчас блокируют.
 */
function getVisitorId() {
    try {
        let id = localStorage.getItem(VISITOR_KEY);
        if (!id) {
            id = (crypto.randomUUID && crypto.randomUUID())
                || `v-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
            localStorage.setItem(VISITOR_KEY, id);
        }
        return id;
    } catch {
        // localStorage заблокирован (приватный режим Safari, строгий ITP и т.д.).
        // Сервер откатывается на ip+UA fingerprint.
        return '';
    }
}

/**
 * Создаёт инстанс виджета для одного input. Возвращает handle, чей
 * `cleanup()` откатывает каждый side-effect (DOM wrap, listeners, AbortControllers,
 * debounce-таймер, document-level outside-click). Вызывается как из `bootstrap()`
 * (auto-mount), так и из `Dokasearch.init({})` (программно).
 *
 * @param {HTMLInputElement} input
 * @param {{
 *     publicKey: string,
 *     baseUrl: string,
 *     debounceMs: number,
 *     minLength: number,
 *     perPage: number,
 *     theme?: 'light'|'dark',
 *     layout?: 'overlay',
 *     renderHit?: (hit: object, defaultEl: Element) => Element|null|undefined,
 * }} options
 * @returns {{input: HTMLInputElement, cleanup: () => void, __dead?: boolean}}
 */
function createWidget(input, options) {
    if (input.__doka) {
        console.warn('[doka] input already mounted; init() is a no-op');
        return { input, cleanup: () => {}, __dead: true };
    }
    input.__doka = true;
    const cleanups = [];

    const api = new WidgetApi(options.publicKey, options.baseUrl, globalThis.fetch.bind(globalThis), getVisitorId());
    const sessionId = getSessionId();
    let searchAbort = null;
    let suggestAbort = null;
    // Generation token защищает от того, чтобы устаревшие ответы перезаписали
    // результаты более нового нажатия клавиши, когда запрос был in-flight
    // (status: 'fulfilled' до того как его прервали) в момент срабатывания abort().
    // AbortController отклоняет только pending fetch'и; resolved-но-ещё-не-применённые
    // ответы его обходят. Каждый run() инкрементит gen; состояние пишет только текущий gen.
    let callGen = 0;

    const isInline = options.layout === 'inline';

    const store = createStore({
        query: '',
        suggestions: [],
        hits: [],
        total: 0,
        tookMs: 0,
        activeIndex: -1,
        loading: false,
        error: null,
        queryId: null,
        // Состояние inline-режима (overlay-режим их игнорирует)
        facets: {},
        appliedFilters: {},
        page: 1,
        schemaFacets: options.schemaFacets || [],
        // BL-H1 — widget config (в частности brand_badge). По умолчанию
        // `mandatory`, чтобы атрибуция побеждала при любом сбое запроса.
        brandBadge: 'mandatory',
    });

    // BL-H1 — fire-and-forget запрос config при mount. Сбои откатываются
    // на default `mandatory`, уже лежащий в state; мы никогда не блокируем
    // boot виджета в ожидании этого.
    api.widgetConfig().then(cfg => {
        if (cfg && typeof cfg.brand_badge === 'string') {
            store.set({ brandBadge: cfg.brand_badge });
        }
    }).catch(() => {
        // Молча — store сохраняет default `mandatory`.
    });

    const selectHit = async (hit, position) => {
        const url = hit.source && typeof hit.source.product_url === 'string' ? hit.source.product_url : null;
        const queryId = store.get().queryId;
        if (queryId && hit.id) {
            try {
                await api.trackClick({
                    queryId,
                    productId: String(hit.id),
                    position,
                    sessionId,
                });
            } catch (err) {
                console.warn('[doka] click track failed', err);
            }
        }
        if (url) {
            window.location.href = url;
        }
    };

    const searchBox = new SearchBox(input, { debounceMs: options.debounceMs });
    if (options.theme === 'dark') {
        searchBox.container.setAttribute('data-theme', 'dark');
    }
    if (isInline) {
        searchBox.container.classList.add('doka-inline');
    }

    let dropdown = null;
    let inlinePanel = null;

    if (isInline) {
        inlinePanel = new InlinePanel(searchBox.container, {
            onFilterChange: (field, value, checked) => onFilterToggle(field, value, checked),
            onPageChange: (p) => onPageChange(p),
            onClear: () => onClearFilters(),
            onSelectHit: selectHit,
            perPage: options.perPage,
        });
    } else {
        dropdown = new Dropdown(searchBox.container, {
            onSelectSuggestion: (text) => {
                searchBox.setValue(text);
                run(text);
            },
            onSelectHit: selectHit,
            renderHit: options.renderHit,
        });
        searchBox.input.setAttribute('aria-controls', dropdown.elementId());
    }

    // Кладём teardown DOM/abort ПЕРВЫМ, чтобы он сработал ПОСЛЕДНИМ. Согласно code-review
    // SF#2 (Chunk 2): LIFO-порядок `cleanups.pop()` означает, что отцепление listener'ов
    // должно произойти до unwrap() — если unwrap() выполнится, пока listeners ещё
    // прицеплены, поставленное в очередь событие могло бы сработать на перемещённом/отцепленном input.
    cleanups.push(() => {
        if (searchAbort) searchAbort.abort();
        if (suggestAbort) suggestAbort.abort();
        searchBox.cancelDebounce();
        searchBox.unwrap();
        try { delete input.__doka; } catch { input.__doka = undefined; }
    });

    const unsubscribeStore = store.subscribe((state) => {
        if (inlinePanel) {
            inlinePanel.render(state);
        } else {
            dropdown.render(state);
            searchBox.setExpanded(dropdown.isOpen());
            const id = state.activeIndex >= 0
                ? `${dropdown.elementId()}-item-${state.activeIndex}`
                : null;
            searchBox.setActiveDescendant(id);
        }
    });
    cleanups.push(unsubscribeStore);

    const handle = {
        input,
        cleanup: () => {
            while (cleanups.length) {
                const fn = cleanups.pop();
                try { fn(); } catch (e) { console.warn('[doka] cleanup step threw', e); }
            }
        },
    };
    const guardAlive = () => {
        if (!document.contains(input)) {
            handle.cleanup();
            return false;
        }
        return true;
    };

    const onInput = (q) => {
        if (!guardAlive()) return;
        if (q.length < options.minLength) {
            // Отменяем все in-flight запросы И инкрементим generation, чтобы
            // поздно-резолвящийся ответ из времён до сброса был проигнорирован.
            if (searchAbort) searchAbort.abort();
            if (suggestAbort) suggestAbort.abort();
            callGen++;
            store.set({
                query: q,
                suggestions: [],
                hits: [],
                total: 0,
                tookMs: 0,
                activeIndex: -1,
                loading: false,
                error: null,
                queryId: null,
                facets: {},
                appliedFilters: {},
                page: 1,
            });
            if (dropdown) dropdown.close();
            return;
        }
        if (isInline) {
            // Фильтры переносятся в пределах того же запроса; страница сбрасывается на 1 при новом запросе.
            store.set({ page: 1 });
            runInstant(q);
        } else {
            run(q);
        }
    };
    const removeInputHandler = searchBox.onInputDebounced(onInput);
    cleanups.push(removeInputHandler);

    if (!isInline) {
        const onFocus = () => {
            if (!guardAlive()) return;
            const s = store.get();
            if (s.suggestions.length > 0 || s.hits.length > 0) dropdown.open();
        };
        const removeFocusHandler = searchBox.onFocus(onFocus);
        cleanups.push(removeFocusHandler);

        const onKeyDown = (e) => {
            if (!guardAlive()) return;
            const s = store.get();
            const items = [...s.suggestions.map(x => ({ kind: 'sug', x })), ...s.hits.map(x => ({ kind: 'hit', x }))];
            const action = handleKey(e.key, { activeIndex: s.activeIndex, items });

            if (action.type === 'move') {
                if (!dropdown.isOpen()) return;
                e.preventDefault();
                store.set({ activeIndex: action.activeIndex });
            } else if (action.type === 'close') {
                dropdown.close();
                store.set({ activeIndex: -1 });
            } else if (action.type === 'select') {
                e.preventDefault();
                const picked = items[action.index];
                if (picked.kind === 'sug') {
                    searchBox.setValue(picked.x.text);
                    run(picked.x.text);
                } else {
                    const hit = picked.x;
                    const hitIndex = s.hits.indexOf(hit);
                    selectHit(hit, hitIndex + 1);
                }
            }
        };
        const removeKeyDownHandler = searchBox.onKeyDown(onKeyDown);
        cleanups.push(removeKeyDownHandler);

        const removeOutsideClick = dropdown.bindOutsideClick(searchBox.container, () => dropdown.close());
        cleanups.push(removeOutsideClick);
    }

    function onFilterToggle(field, value, checked) {
        const current = store.get().appliedFilters || {};
        const next = { ...current };
        const list = new Set((next[field] || []).map(String));
        if (checked) list.add(value); else list.delete(value);
        if (list.size === 0) {
            delete next[field];
        } else {
            next[field] = Array.from(list);
        }
        store.set({ appliedFilters: next, page: 1 });
        runInstant(store.get().query);
    }

    function onPageChange(p) {
        store.set({ page: Math.max(1, p) });
        runInstant(store.get().query);
    }

    function onClearFilters() {
        store.set({ appliedFilters: {}, page: 1 });
        runInstant(store.get().query);
    }

    /**
     * Search runner inline-режима — принимает filters + offset, обновляет facets.
     * Переиспользует тот же паттерн generation token, что и overlay `run()`.
     */
    async function runInstant(query) {
        if (searchAbort) searchAbort.abort();
        searchAbort = new AbortController();
        const myGen = ++callGen;

        const state = store.get();
        const page = state.page || 1;
        const offset = (page - 1) * options.perPage;

        store.set({ query, loading: true, error: null });

        let aggregations = {};
        let hits = [];
        let total = 0;
        let tookMs = 0;
        let queryId = null;
        let error = null;

        try {
            const res = await api.search(query, {
                limit: options.perPage,
                offset,
                filters: state.appliedFilters,
                signal: searchAbort.signal,
            });
            hits = res.hits || [];
            total = res.total ?? 0;
            tookMs = res.took_ms ?? 0;
            queryId = res.query_id ?? null;
            aggregations = res.aggregations || {};
        } catch (e) {
            if (e?.name === 'AbortError') return;
            if (e instanceof ApiError && e.status >= 500) {
                error = 'unavailable';
            } else {
                error = 'unavailable';
            }
        }

        if (myGen !== callGen) return;

        store.set({
            hits,
            total,
            tookMs,
            queryId,
            loading: false,
            error,
            facets: aggregations,
        });
    }

    async function run(query) {
        if (searchAbort) searchAbort.abort();
        if (suggestAbort) suggestAbort.abort();
        searchAbort = new AbortController();
        suggestAbort = new AbortController();
        const myGen = ++callGen;

        store.set({ query, loading: true, error: null });
        dropdown.open();

        const [searchRes, suggestRes] = await Promise.allSettled([
            api.search(query, { limit: options.perPage, signal: searchAbort.signal }),
            api.suggest(query, { limit: 8, signal: suggestAbort.signal }),
        ]);

        // Защита от устаревшего ответа: если более новое нажатие клавиши вызвало run(),
        // пока мы await'или, это generation уже не текущий. Отбрасываем обновление —
        // состоянием владеет более новый вызов.
        if (myGen !== callGen) return;

        let hits = [];
        let total = 0;
        let queryId = null;
        let error = null;

        if (searchRes.status === 'fulfilled') {
            hits = searchRes.value.hits || [];
            total = searchRes.value.total ?? 0;
            queryId = searchRes.value.query_id ?? null;
        } else if (searchRes.reason instanceof ApiError && searchRes.reason.status >= 500) {
            error = 'unavailable';
        } else if (searchRes.reason?.name === 'AbortError') {
            return; // прервано до завершения; отбрасываем это обновление
        }

        const suggestions = (suggestRes.status === 'fulfilled' ? (suggestRes.value.items || []) : []);

        store.set({
            suggestions,
            hits,
            total,
            activeIndex: -1,
            loading: false,
            error,
            queryId,
        });
    }

    return handle;
}

function bootstrap() {
    const config = readConfig();
    if (!config.publicKey) {
        console.warn('[doka] missing data-public-key on widget script tag');
        return;
    }
    if (!config.selector) {
        console.warn('[doka] missing data-input on widget script tag');
        return;
    }
    const inputs = document.querySelectorAll(config.selector);
    if (inputs.length === 0) {
        console.warn(`[doka] no element matched data-input="${config.selector}"`);
        return;
    }
    // Отслеживаем handle'ы auto-mount в Dokasearch._auto, чтобы вызывающий код (demo HTML,
    // SPA-хосты, которые хотят переинициализировать после auto-mount) мог снести их
    // через Dokasearch.destroy() вместо ручной зачистки DOM.
    // Code-review Blocker#1 + SF#3 (Chunk 2): без этого вызов
    // Dokasearch.init() после auto-mount оставлял исходные listeners
    // прицепленными и дублировал срабатывание на каждом нажатии клавиши.
    const reg = window.Dokasearch && window.Dokasearch._auto;
    inputs.forEach((input) => {
        const handle = createWidget(input, config);
        if (reg && !handle.__dead) reg.push(handle);
    });
}

function inferBaseUrlFromScript() {
    const script = document.currentScript ?? lastWidgetScript();
    if (!script) return '';
    const src = script.getAttribute('src') || '';
    try {
        const u = new URL(src, window.location.href);
        return `${u.protocol}//${u.host}`;
    } catch {
        return '';
    }
}

function deadHandle() {
    return { __dead: true, cleanup: () => {} };
}

/**
 * Программный API для SPA-хостов и онбординга в Cabinet. Та же фабрика,
 * что и auto-mount, только options приходят с места вызова, а не из
 * `data-*` атрибутов.
 */
const Dokasearch = {
    version: '0.2.0',

    /**
     * Реестр handle'ов, созданных путём auto-mount (`bootstrap()`).
     * Внутренний, но доступный — demo HTML + SPA-хосты, которые хотят отцепить
     * auto-mount перед вызовом `init({})`, должны пройтись по нему и сделать
     * `destroy(handle)` для каждой записи, затем `_auto.length = 0`. Не часть
     * документированного публичного API; может быть переименован.
     * @type {Array<{input: HTMLInputElement, cleanup: () => void}>}
     */
    _auto: [],

    /**
     * Уничтожает каждый handle, зарегистрированный в `_auto`. Удобство для
     * паттерна «снести auto-mount перед переинициализацией».
     */
    destroyAll() {
        while (this._auto.length) {
            const h = this._auto.shift();
            this.destroy(h);
        }
    },

    /**
     * @param {{
     *     selector: string,
     *     publicKey: string,
     *     baseUrl?: string,
     *     debounceMs?: number,
     *     minLength?: number,
     *     perPage?: number,
     *     theme?: 'light'|'dark',
     *     layout?: 'overlay'|'inline',
     *     schemaFacets?: Array<{field: string, label: string}>,
     *     renderHit?: (hit: object, defaultEl: Element) => Element|null|undefined,
     * }} opts
     * @returns {{input: HTMLInputElement, cleanup: () => void, __dead?: boolean}}
     */
    init(opts) {
        const o = opts || {};
        if (!o.selector) {
            console.warn('[doka] init() requires options.selector');
            return deadHandle();
        }
        if (!o.publicKey) {
            console.warn('[doka] init() requires options.publicKey');
            return deadHandle();
        }
        let layout = 'overlay';
        if (o.layout === 'inline') {
            layout = 'inline';
        } else if (o.layout && o.layout !== 'overlay') {
            console.warn(`[doka] init({layout:"${o.layout}"}) not supported in this version; falling back to overlay`);
        }
        const input = document.querySelector(o.selector);
        if (!input) {
            console.warn(`[doka] init(): no element matched selector "${o.selector}"`);
            return deadHandle();
        }
        return createWidget(input, {
            publicKey: o.publicKey,
            baseUrl: o.baseUrl || inferBaseUrlFromScript(),
            debounceMs: typeof o.debounceMs === 'number' ? o.debounceMs : 250,
            minLength: typeof o.minLength === 'number' ? o.minLength : 2,
            perPage: typeof o.perPage === 'number' ? Math.min(o.perPage, 24) : 12,
            theme: o.theme === 'dark' ? 'dark' : 'light',
            layout,
            schemaFacets: Array.isArray(o.schemaFacets) ? o.schemaFacets : [],
            renderHit: typeof o.renderHit === 'function' ? o.renderHit : undefined,
        });
    },

    /**
     * Уничтожает widget handle. Идемпотентно. Безопасно вызывать на dead handle'ах.
     */
    destroy(handle) {
        if (!handle || typeof handle.cleanup !== 'function') return;
        try {
            handle.cleanup();
        } catch (e) {
            console.warn('[doka] destroy threw', e);
        }
    },
};

window.Dokasearch = Dokasearch;

// Открываем внутренний escape hatch для SPA-хостов (не афишируется в merchant-доках;
// публичный API init({}) выше). Вызываемо из консоли для разработки.
window.DokaSearchBootstrap = bootstrap;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
} else {
    bootstrap();
}
